import { useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { useVersioning } from '../../versioning'
import { getPermanentPageId } from '../../shares/utils'
import { useAnalytics, RouteParams } from '@sketch/modules-common'
import type { GoToArtboardEventActionType } from './types'

/**
 * Returns a navigateToSection callback that focuses the canvas on a single
 * Section via the SECTION route, hiding every other top-level entity - the
 * Section equivalent of useNavigateToFrame.
 */
export function useNavigateToSection() {
  const history = useHistory()
  const { getPathname, currentVersion } = useVersioning()
  const { trackEvent } = useAnalytics()
  const { shareID, pageUUID: pageUUIDFromUrl } =
    useParams<RouteParams<'SHARE_PAGE_CANVAS_VIEW'>>()

  // On the default (first) page the URL omits the pageUUID; getPermanentPageId
  // falls back to the first document page so the PAGE_SECTION path is well-formed
  // (otherwise it becomes ".../p//sc/..." and navigation silently does nothing).
  const pageUUID = getPermanentPageId(pageUUIDFromUrl, currentVersion)

  return useCallback(
    (sectionUUID: string, actionType: GoToArtboardEventActionType) => {
      if (!pageUUID) return

      trackEvent('CANVAS - open artboard', { actionType })
      history.push(
        getPathname({
          routeKey: 'PAGE_SECTION',
          routeParams: { shareID, pageUUID, sectionUUID },
        })
      )
    },
    [trackEvent, history, getPathname, shareID, pageUUID]
  )
}
